import { todayPT } from "@homebound/activesupport";
import { InlineFeedbackBannerType } from "@homebound/beam";
import { TaskStatus, TradePartnerTaskStatus } from "src/generated/graphqlTypes";
import { pluralize } from "src/utils";
import { formatWithShortYear } from "src/utils/dates";
import { ScheduleDraftMode_PlanTaskFragment } from "./ScheduleDraftMode.generated";

export type DraftTaskAlert = { type: InlineFeedbackBannerType; tagText?: string; description: string };

// How many dates into the future do we consider it a problem if a task is set to start, but doesn't have a confirmed trade
const TRADE_CONFIRMATION_ALERT_THRESHOLD_DAYS = 2;

/**
 * The alerts shown under a task in the read-only draft schedule table.
 *
 * A task can have an overdue alert and a trade confirmation alert at the same time.
 */
export function getDraftTaskAlerts(task: ScheduleDraftMode_PlanTaskFragment): DraftTaskAlert[] {
  return [getOverdueAlert(task), getTradeConfirmationAlert(task)].compact();
}

/**
 * A not-started task is overdue once its start date has passed, and an in-progress task once its end date has passed.
 *
 * This matches the mobile app. We don't use `PlanTask.isStale` because it also counts tasks that start or end today.
 */
function getOverdueAlert(task: ScheduleDraftMode_PlanTaskFragment): DraftTaskAlert | undefined {
  const { status, startDate, endDate, isCriticalPath } = task;
  const today = todayPT();
  const isNotStarted = status.code === TaskStatus.NotStarted;
  const overdueSince = isNotStarted ? startDate : status.code === TaskStatus.Complete ? undefined : endDate;
  if (!overdueSince?.isBefore(today)) return undefined;

  const days = overdueSince.until(today).days;
  const description = isNotStarted
    ? `Planned to start ${formatWithShortYear(startDate)} and not started. Update the status or move the dates.`
    : `Planned to finish ${formatWithShortYear(endDate)} and still in progress.`;
  // TODO: [FIELD-613] Saying the task slips Vertical Complete needs the dependency chain check
  return {
    type: "error",
    tagText: `${days} ${pluralize(days, "day", "days")} overdue${isCriticalPath ? " • Critical path" : ""}`,
    description,
  };
}

/** The trade hasn't confirmed a task that starts within the next 48 hours. */
function getTradeConfirmationAlert(task: ScheduleDraftMode_PlanTaskFragment): DraftTaskAlert | undefined {
  const { status, startDate, tradePartner, tradePartnerStatus, tradePartnerAvailabilityRequests } = task;
  if (
    !tradePartner ||
    status.code === TaskStatus.Complete ||
    startDate.isAfter(todayPT().add({ days: TRADE_CONFIRMATION_ALERT_THRESHOLD_DAYS }))
  ) {
    return undefined;
  }

  switch (tradePartnerStatus.code) {
    case TradePartnerTaskStatus.NotSent:
    case TradePartnerTaskStatus.NeedsReconfirmation:
      return {
        type: "error",
        description: `Starts ${formatWithShortYear(startDate)}. ${tradePartner.name} hasn't been asked to confirm.`,
      };
    case TradePartnerTaskStatus.NeedsConfirmation: {
      const sentOn = tradePartnerAvailabilityRequests
        ?.filter((r) => r.tradePartner.id === tradePartner.id)
        .last?.createdAt.toPlainDate();
      return {
        type: "warning",
        description: `${sentOn ? `Request sent ${formatWithShortYear(sentOn)}. ` : ""}Waiting on ${tradePartner.name} to confirm.`,
      };
    }
    default:
      return undefined;
  }
}
